import type { Location, NavigateFunction } from 'react-router-dom'
import type { User } from '@/stores/auth'
import { navigationHelpers, routePaths } from './router'

interface NavigationState {
  from?: { pathname: string; search?: string }
  [key: string]: unknown
}

interface NavigationOptions {
  replace?: boolean
  state?: NavigationState
}

const PENDING_RETURN_TO_KEY = 'auth.pendingReturnTo'

export function getValidReturnTo(returnTo: string | null | undefined) {
  if (
    !returnTo?.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.includes('\\')
  ) {
    return undefined
  }

  const pathname = returnTo.split(/[?#]/, 1)[0]

  return navigationHelpers.isProtectedRoute(pathname) ? returnTo : undefined
}

export function stashPendingReturnTo(returnTo: string | null | undefined) {
  const validReturnTo = getValidReturnTo(returnTo)

  if (validReturnTo) {
    sessionStorage.setItem(PENDING_RETURN_TO_KEY, validReturnTo)
  }

  return validReturnTo
}

export function consumePendingReturnTo() {
  const returnTo = sessionStorage.getItem(PENDING_RETURN_TO_KEY)
  sessionStorage.removeItem(PENDING_RETURN_TO_KEY)

  return getValidReturnTo(returnTo)
}

/**
 * Central navigation utilities for handling redirects and navigation logic
 */
export class NavigationManager {
  private navigate: NavigateFunction
  private location: Location

  constructor(navigate: NavigateFunction, location: Location) {
    this.navigate = navigate
    this.location = location
  }

  /**
   * Redirect user after successful login
   */
  redirectAfterLogin(
    user?: User,
    returnTo?: string | null,
    options: NavigationOptions = {}
  ) {
    const validReturnTo = getValidReturnTo(returnTo)

    // Check if onboarding is completed first
    if (user && !user.onboardingCompletedAt) {
      if (validReturnTo) {
        stashPendingReturnTo(validReturnTo)
      }

      // Redirect to onboarding flow (not register, since account already exists)
      this.navigate('/onboarding', {
        replace: true,
        state: {
          message: 'Please complete your onboarding to continue',
        },
        ...options,
      })
      return
    }

    // Get intended destination from location state
    const from = this.location.state?.from?.pathname
    const fromSearch = this.location.state?.from?.search || ''

    // Don't redirect back to onboarding if user has completed it
    if (from === '/onboarding' && !validReturnTo) {
      const pendingReturnTo = consumePendingReturnTo()
      const destination =
        pendingReturnTo ?? navigationHelpers.getDefaultRouteForUser(user)

      this.navigate(destination, {
        replace: true,
        ...options,
      })
      return
    }

    // Determine where to redirect
    let destination: string

    if (validReturnTo) {
      consumePendingReturnTo()
      destination = validReturnTo
    } else if (from && navigationHelpers.isProtectedRoute(from)) {
      // Redirect to originally intended protected route
      destination = `${from}${fromSearch}`
    } else {
      const pendingReturnTo = consumePendingReturnTo()

      destination =
        pendingReturnTo ?? navigationHelpers.getDefaultRouteForUser(user)
    }

    this.navigate(destination, {
      replace: true,
      ...options,
    })
  }

  /**
   * Redirect to login with current location preserved
   */
  redirectToLogin(options: NavigationOptions = {}) {
    this.navigate(routePaths.login, {
      replace: true,
      state: {
        from: {
          pathname: this.location.pathname,
          search: this.location.search,
        },
      },
      ...options,
    })
  }

  /**
   * Redirect after logout
   */
  redirectAfterLogout(options: NavigationOptions = {}) {
    this.navigate(routePaths.login, {
      replace: true,
      state: undefined, // Clear any previous state
      ...options,
    })
  }

  /**
   * Navigate to a protected route with role checking
   */
  navigateToProtectedRoute(
    path: string,
    userRole?: string,
    options: NavigationOptions = {}
  ) {
    // Check if user has access to this route
    if (this.canAccessRoute(path, userRole)) {
      this.navigate(path, options)
    } else {
      // Redirect to access denied or default route
      console.warn('Access denied to route:', path, 'for role:', userRole)
      this.navigate(routePaths.dashboard, { replace: true })
    }
  }

  /**
   * Check if user can access a specific route
   */
  canAccessRoute(path: string, userRole?: string): boolean {
    // Public routes are always accessible
    if (navigationHelpers.isPublicRoute(path)) {
      return true
    }

    // For protected routes, check role requirements
    // This would be enhanced when we have more detailed route permissions
    const routeRoleRequirements = this.getRouteRoleRequirements(path)

    if (routeRoleRequirements.length === 0) {
      // No specific role required, any authenticated user can access
      return true
    }

    if (!userRole) {
      return false
    }

    return routeRoleRequirements.includes(userRole.toLowerCase())
  }

  /**
   * Get role requirements for a specific route
   */
  private getRouteRoleRequirements(path: string): string[] {
    // Define role requirements for each route
    const roleRequirements: Record<string, string[]> = {
      [routePaths.users]: ['admin'],
      [routePaths.organizations]: ['admin', 'manager'],
      // Add more as needed
    }

    return roleRequirements[path] || []
  }

  /**
   * Navigate with breadcrumb support
   */
  navigateWithBreadcrumb(
    path: string,
    breadcrumb?: string,
    options: NavigationOptions = {}
  ) {
    const state = {
      ...options.state,
      breadcrumb: breadcrumb || this.getBreadcrumbForPath(path),
      previousPath: this.location.pathname,
    }

    this.navigate(path, {
      ...options,
      state,
    })
  }

  /**
   * Go back to previous route or default
   */
  goBack(defaultPath: string = routePaths.dashboard) {
    const previousPath = this.location.state?.previousPath

    if (previousPath && previousPath !== this.location.pathname) {
      this.navigate(previousPath)
    } else {
      this.navigate(defaultPath)
    }
  }

  /**
   * Get breadcrumb text for a path
   */
  private getBreadcrumbForPath(path: string): string {
    const breadcrumbs: Record<string, string> = {
      [routePaths.dashboard]: 'Dashboard',
      [routePaths.users]: 'User Management',
      [routePaths.organizations]: 'Organizations',
      [routePaths.products]: 'Product Catalog',
      [routePaths.settings]: 'Settings',
      [routePaths.login]: 'Login',
      [routePaths.register]: 'Register',
      [routePaths.forgotPassword]: 'Forgot Password',
    }

    return breadcrumbs[path] || path
  }

  /**
   * Check if we're currently on a specific route
   */
  isCurrentRoute(path: string): boolean {
    return this.location.pathname === path
  }

  /**
   * Get current route info
   */
  getCurrentRouteInfo() {
    return {
      pathname: this.location.pathname,
      isPublic: navigationHelpers.isPublicRoute(this.location.pathname),
      isProtected: navigationHelpers.isProtectedRoute(this.location.pathname),
      breadcrumb: this.getBreadcrumbForPath(this.location.pathname),
      state: this.location.state,
    }
  }
}

/**
 * Hook to get navigation manager instance
 */
export function useNavigationManager(
  navigate: NavigateFunction,
  location: Location
) {
  return new NavigationManager(navigate, location)
}

/**
 * Redirect configurations for different scenarios
 */
export const redirectConfigs = {
  login: {
    success: {
      replace: true,
      clearState: false,
    },
    failure: {
      replace: false,
      preserveState: true,
    },
  },
  logout: {
    replace: true,
    clearState: true,
  },
  unauthorized: {
    replace: true,
    preserveLocation: true,
  },
  sessionExpired: {
    replace: true,
    preserveLocation: true,
    message: 'Your session has expired. Please log in again.',
  },
} as const
